import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Image, Launch, Layers, Settings } from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";

import { TreeEnvironmentRef, TreeItem, TreeItemIndex } from "ui/Tree";

import { uploadFileToS3 } from "fond/api";
import {
  useCreateImageOverlayMutation,
  useDeleteImageOverlayMutation,
  useLazyGetImageOverlayPresignedUploadQuery,
  useUpdateImageOverlayMutation,
} from "fond/api/overlaysSlice";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { ImageOverlayModal, ImageOverlayResult } from "fond/imageOverlay";
import { InferredIcon } from "fond/map/Legend/InferredIcon";
import { Container as IconContainer } from "fond/map/Legend/InferredIcon/InferredIcon.styles";
import { LegendButton } from "fond/map/Legend/Legend.styles";
import { MapContext } from "fond/map/MapProvider";
import { getCurrentProject, getCurrentProjectView, setLayerVisibility } from "fond/project/redux";
import { GroupConfig, MapLayerConfig, Store } from "fond/types";
import { BBox } from "fond/types/geometry";
import { LayerConfig, LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { generateTreeItems, isVisible } from "fond/utils/configurations";
import { useAppDispatch } from "fond/utils/hooks";
import { Actions } from "fond/utils/permissions";
import { isMultiProject } from "fond/utils/project";
import { BlockSpinner, NonIdealState } from "fond/widgets";

import BaseLegend from "../Legend/BaseLegend";

import OverlaysSettingModal from "./OverlaysSettingModal";
import { useOverlaysWithConfigs } from "./useOverlaysWithConfigs";

export type LegendItemType = MapLayerConfig | LayerConfig | SublayerConfig | LayerStyle | GroupConfig;

const OVERLAY_TREE_ID = "overlayTreeId";

const Overlays: React.FC = () => {
  const dispatch = useAppDispatch();

  const environmentRef = useRef<TreeEnvironmentRef>(undefined);
  const versionId = useSelector((state: Store) => state.project.versionId);
  const projectId = useSelector((state: Store) => state.project.projectId);
  const layerView = useSelector((state: Store) => getCurrentProjectView(state.project)?.layers ?? {});
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const { map } = useContext(MapContext);
  const [showOverlays, setShowOverlays] = useState<boolean>(false);
  const [showImageOverlays, setShowImageOverlays] = useState<boolean>(false);
  const [imageOverlayBbox, setImageOverlayBbox] = useState<BBox | null>(null);
  const { overlays: mergedConfig, overlayMetadata, isLoading } = useOverlaysWithConfigs(versionId, project);
  const canEdit = usePermissionCheck(Actions.CUSTOM_OVERLAYS, project?.Permission.Level);
  const { enqueueSnackbar } = useSnackbar();
  const [createImageOverlay] = useCreateImageOverlayMutation();
  const [getPresignedUpload] = useLazyGetImageOverlayPresignedUploadQuery();
  const [updateImageOverlay] = useUpdateImageOverlayMutation();
  const [deleteImageOverlay] = useDeleteImageOverlayMutation();
  const multiProjectId = project && isMultiProject(project) ? project.ID : undefined;
  const targetType = multiProjectId ? ("multi_project" as const) : ("version" as const);
  const targetId = multiProjectId || versionId || "";

  const handleOpenImageOverlay = useCallback(() => {
    if (map) {
      const bounds = map.getBounds();
      setImageOverlayBbox([
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getNorth()],
      ]);
    }
    setShowImageOverlays(true);
  }, [map]);

  const handleImageOverlayConfirm = useCallback(
    async (data: ImageOverlayResult) => {
      // Extract mime type from data URL (e.g. "data:image/png;base64,..." → "image/png")
      const mimeType = data.imageUrl.match(/^data:(.*?);/)?.[1] ?? "image/png";
      let overlayId: string | null = null;

      try {
        // 1. Create the image overlay record
        const overlay = await createImageOverlay({
          name: data.imageName,
          imageCorners: data.imageCorners,
          opacity: data.opacity,
          mimeType,
          targetType,
          targetId,
        }).unwrap();
        overlayId = overlay.ID;

        // 2. Get presigned upload URL
        const presigned = await getPresignedUpload({ imageOverlayId: overlay.ID }).unwrap();

        // 3. Upload image to S3
        const blob = await fetch(data.imageUrl).then((r) => r.blob());
        const file = new File([blob], data.imageName, { type: mimeType });
        await new Promise<void>((resolve, reject) => {
          uploadFileToS3({
            presignedPost: presigned,
            file,
            onProgress: () => {},
            onComplete: () => resolve(),
            onError: (error) => reject(error),
          });
        });

        // 4. Mark as uploaded
        await updateImageOverlay({ imageOverlayId: overlay.ID, targetId, Uploaded: true }).unwrap();

        enqueueSnackbar(`Image overlay "${data.imageName}" created successfully.`, { variant: "success" });
      } catch (error) {
        // Clean up orphaned record if it was created but upload failed
        if (overlayId) {
          try {
            await deleteImageOverlay({ imageOverlayId: overlayId, targetId }).unwrap();
          } catch {
            // Cleanup failure is silent — orphaned records with Uploaded=false
            // are already filtered out by getImageOverlays
          }
        }
        enqueueSnackbar(`Failed to create image overlay "${data.imageName}".`, { variant: "error" });
        throw error;
      }
    },
    [createImageOverlay, getPresignedUpload, updateImageOverlay, deleteImageOverlay, targetType, targetId, enqueueSnackbar]
  );
  const isEntityVisible = useCallback(
    (key: string) => (mergedConfig ? isVisible(mergedConfig, { id: key, layerView }) : true),
    [mergedConfig, layerView]
  );

  /**
   * Generate the data source for the tree based on the merged overlay configurations
   */
  const treeItems = useMemo(() => (mergedConfig ? generateTreeItems({ config: mergedConfig, showStyles: false }) : {}), [mergedConfig]);

  /**
   * Returns the TreeItem's title value based on the LegendItemType
   */
  const getItemTitle = useCallback(({ data: itemData }: TreeItem<LegendItemType>) => {
    if (itemData.Type === "STYLE") return itemData.Name;
    if (itemData.Type === "MapLayerConfig") return "";
    return itemData.Label;
  }, []);

  const renderItemLeftAdornment = useCallback(
    ({ data }: TreeItem<LegendItemType>) => {
      if (data.Type !== "GROUP" && data.Type !== "MapLayerConfig" && mergedConfig) {
        // Image overlays have no styles — show an image icon instead
        if (data.ID.startsWith("image-overlay-")) {
          return (
            <IconContainer>
              <Image sx={{ width: 18, height: 18, color: "text.secondary" }} />
            </IconContainer>
          );
        }
        return <InferredIcon entity={data as LayerConfig | SublayerConfig | LayerStyle} config={mergedConfig} />;
      }

      return null;
    },
    [mergedConfig]
  );

  const renderItemRightAdornment = useCallback(
    ({ data }: TreeItem<LegendItemType>) => {
      // Only show navigation icon for overlay groups (not the parent "Overlays" group)
      if (data.Type === "GROUP" && data.ID.startsWith("overlay-group-")) {
        const metadata = overlayMetadata[data.ID];
        if (!metadata) return null;

        return (
          <Tooltip title="Open source project" placement="left">
            <IconButton
              component={Link}
              to={`/project/${metadata.sourceProjectId}/`}
              size="small"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ padding: 0.5 }}
            >
              <Launch fontSize="small" sx={{ fontSize: "small" }} />
            </IconButton>
          </Tooltip>
        );
      }

      return null;
    },
    [overlayMetadata]
  );

  const onItemVisibilityChanges = useCallback(
    (itemsToShow: TreeItemIndex[], itemsToHide: TreeItemIndex[]) => {
      if (itemsToShow.length > 0) {
        dispatch(setLayerVisibility(projectId, itemsToShow, true));
      }
      if (itemsToHide.length > 0) {
        dispatch(setLayerVisibility(projectId, itemsToHide, false));
      }
    },
    [dispatch, projectId]
  );

  // Expand the parent "Overlays" group and all its direct children by default
  const defaultExpanded = useMemo(() => {
    if (!mergedConfig) return [];

    const expanded: string[] = [];

    // Add the parent "Overlays" group
    mergedConfig.MapChildren.forEach((groupId) => {
      expanded.push(groupId);

      // Add all its children (individual overlay groups)
      const parentGroup = mergedConfig.Data.entities[groupId];
      if (parentGroup?.Type === "GROUP" && parentGroup.Children) {
        expanded.push(...parentGroup.Children);
      }
    });

    return expanded;
  }, [mergedConfig]);
  const expand = useMemo(() => ({ defaultExpanded }), [defaultExpanded]);

  // Show loading spinner while overlays are being loaded
  if (isLoading) {
    return <BlockSpinner />;
  }

  // If no overlays exist, show NonIdealState with call to action
  if (!mergedConfig) {
    return (
      <>
        <NonIdealState
          size="small"
          icon={<Layers />}
          title="No Overlays"
          description={
            canEdit
              ? "Add overlays to view layers from other projects on top of your current project."
              : "There are no overlays available for this project."
          }
          action={
            canEdit ? (
              <Button variant="contained" color="primary" onClick={() => setShowOverlays(true)} startIcon={<Settings />} size="small">
                Manage Overlays
              </Button>
            ) : undefined
          }
        />
        {canEdit && (
          <OverlaysSettingModal
            open={showOverlays}
            onClose={() => setShowOverlays(false)}
            onOpenImageOverlay={() => {
              setShowOverlays(false);
              handleOpenImageOverlay();
            }}
          />
        )}
        <ImageOverlayModal
          open={showImageOverlays}
          bbox={imageOverlayBbox}
          onClose={() => setShowImageOverlays(false)}
          onConfirm={handleImageOverlayConfirm}
        />
      </>
    );
  }

  return (
    <>
      <BaseLegend
        environmentRef={environmentRef}
        treeId={OVERLAY_TREE_ID}
        treeItems={treeItems}
        getItemTitle={getItemTitle}
        getItemTitleClass={(item) => `rct-tree-item-button-${item.data?.Type}`}
        getItemClass={(item) => `rct-tree-item-title-${item.data?.Type}`}
        getItemVisibility={(item) => isEntityVisible(item as string)}
        renderItemLeftAdornment={renderItemLeftAdornment}
        renderItemRightAdornment={renderItemRightAdornment}
        onItemVisibilityChanges={onItemVisibilityChanges}
        expand={expand}
        buttons={
          <Tooltip title="Manage Overlays" PopperProps={{ disablePortal: true }}>
            <LegendButton
              variant="outlined"
              aria-label="info"
              size="small"
              onClick={() => setShowOverlays(!showOverlays)}
              data-testid="toggle-overlays-button"
              startIcon={<Settings />}
            >
              Settings
            </LegendButton>
          </Tooltip>
        }
        showTabHeader={canEdit}
      />
      {canEdit && (
        <OverlaysSettingModal
          open={showOverlays}
          onClose={() => setShowOverlays(false)}
          onOpenImageOverlay={() => {
            setShowOverlays(false);
            handleOpenImageOverlay();
          }}
        />
      )}
      <ImageOverlayModal
        open={showImageOverlays}
        bbox={imageOverlayBbox}
        onClose={() => setShowImageOverlays(false)}
        onConfirm={handleImageOverlayConfirm}
      />
    </>
  );
};

export default Overlays;
